import React, { useReducer } from "react";
import { styled } from "@iventis/styles";
import { Autocomplete, Box, Button, ButtonGroup, Checkbox, Collapse, FormControlLabel, FormGroup, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useFilteredLayerIds } from "../../state/hooks/use-filtered-layerids";
import { layerListReducer, initialLayerListState, LayerListState } from "./layer-list-reducer";

const initReducerState = (initialState: LayerListState, layerNames: string[]) => ({
    ...initialState,
    availableLayerIds: layerNames,
    displayedLayers: layerNames,
});

export const LayerListComponent: React.FC<{ layerNames: string[] }> = ({ layerNames }) => {
    const [state, dispatch] = useReducer(layerListReducer, initialLayerListState, (initialState) => initReducerState(initialState, layerNames));
    const { layerIds, removeLayerId, addLayerId, setLayerIds } = useFilteredLayerIds((layerIds) => dispatch({ type: "SET_HIDDEN_LAYERS", layerIds }));

    const handleLayerToggle = (layerId: string, isChecked: boolean) => {
        if (!isChecked) {
            addLayerId(layerId);
        } else {
            removeLayerId(layerId);
        }
    };

    // Handler to show all displayed layers in the sidebar
    const handleCheckAll = () => {
        const ids = [...layerIds];
        const removedIds = ids.filter((layerId) => !state.displayedLayers.includes(layerId));
        setLayerIds(removedIds);
    };

    // Handler to hide all displayed layers in the sidebar
    const handleUncheckAll = () => {
        const ids = new Set(layerIds);
        state.displayedLayers.forEach((layerId) => {
            ids.add(layerId);
        });
        setLayerIds(Array.from(ids));
    };

    return (
        <LayerListContainer>
            <HeaderContainer onClick={() => dispatch({ type: "TOGGLE_EXPANDED" })}>
                <Box className="header-box">
                    <h3>Layers</h3>
                    <FontAwesomeIcon icon={state.expanded ? faChevronDown : faChevronRight} />
                </Box>
            </HeaderContainer>

            <Collapse in={state.expanded} className="collapsing-content">
                <Box className="autocomplete-box">
                    <Autocomplete
                        freeSolo
                        options={state.availableLayerIds}
                        inputValue={state.searchValue}
                        onInputChange={(_, newValue) => dispatch({ type: "SET_SEARCH_VALUE", value: newValue })}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Search layers" placeholder="Type to search" />}
                    />
                </Box>
                {/* Toggle All Buttons */}
                <ButtonGroup variant="outlined" size="small" fullWidth className="select-all-button-group">
                    <Button onClick={handleCheckAll}>Show All</Button>
                    <Button onClick={handleUncheckAll}>Hide All</Button>
                </ButtonGroup>
                <ScrollableLayerList>
                    <FormGroup>
                        {state.displayedLayers.map((layerId) => (
                            <FormControlLabel
                                key={layerId}
                                control={<Checkbox checked={!state.hiddenLayers.has(layerId)} onChange={(e) => handleLayerToggle(layerId, e.target.checked)} />}
                                label={layerId}
                            />
                        ))}
                        {state.displayedLayers.length === 0 && <Box sx={{ p: 1, color: "text.secondary" }}>No matching layers found</Box>}
                    </FormGroup>
                </ScrollableLayerList>
            </Collapse>
        </LayerListContainer>
    );
};

const HeaderContainer = styled.div`
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    width: 100%;

    &:hover {
        opacity: 0.8;
    }
`;

const LayerListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;

    width: 90%;
    background-color: white;
    padding: 10px;
    min-height: 40px;

    z-index: 10;
    border-radius: 6px;

    .header-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .header-box h3 {
        margin: 0px;
        font-size: 1.17em;
        font-weight: bold;
    }

    .autocomplete-box {
        width: 100%;
        margin-bottom: 10px;
    }

    .select-all-button-group {
        margin-bottom: 10px;
    }

    .collapsing-content {
        display: flex;
        flex-direction: column;
        gap: 5px;
        width: 100%;
        margin-top: 15px;
    }
`;

const ScrollableLayerList = styled.div`
    width: 100%;
    max-height: 250px;
    overflow-y: auto;

    & .MuiFormControlLabel-label {
        user-select: none;
    }
`;
